import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import type { SortDirection } from '@/types/api'
import { formatDate, humanize } from '@/utils/format'
import { relativeDue } from '../status'
import type { Loan } from '../types'
import { LoanStateBadge } from './Badges'
import { BookCover } from './BookCover'
import { FineCell } from './FineCell'
import { LoanActions } from './LoanActions'

interface LoanTableProps {
  loans: Loan[] | undefined
  loading?: boolean
  error?: unknown
  onRetry?: () => void
  currency: string
  caption: string
  /** Hide the borrower column (e.g. on a borrower's own page). */
  showBorrower?: boolean
  /** Hide the book column (e.g. on a book's page). */
  showBook?: boolean
  /** Link targets; omit to render plain text. */
  bookHref?: (loan: Loan) => string
  /** Render return/renew/pay buttons. */
  actions?: boolean
  sort?: { key: string | undefined; direction: SortDirection }
  onSortChange?: (key: string) => void
  empty?: ReactNode
}

export function LoanTable({
  loans,
  loading,
  error,
  onRetry,
  currency,
  caption,
  showBorrower = true,
  showBook = true,
  bookHref,
  actions = false,
  sort,
  onSortChange,
  empty,
}: LoanTableProps) {
  const { t } = useTranslation('library')
  const columns: Column<Loan>[] = [
    ...(showBook
      ? [
          {
            key: 'book',
            header: t('Book'),
            sortKey: 'title',
            cell: (l: Loan) => (
              <div className="flex items-center gap-3">
                <BookCover title={l.book.title} src={l.book.coverUrl} size="xs" />
                <div className="min-w-0">
                  {bookHref ? (
                    <Link
                      to={bookHref(l)}
                      className="block truncate font-medium text-ink hover:text-brand-700"
                    >
                      {l.book.title}
                    </Link>
                  ) : (
                    <span className="block truncate font-medium text-ink">{l.book.title}</span>
                  )}
                  <span className="block truncate text-xs text-muted">{l.book.authors}</span>
                </div>
              </div>
            ),
          } satisfies Column<Loan>,
        ]
      : []),
    ...(showBorrower
      ? [
          {
            key: 'borrower',
            header: t('Borrower'),
            cell: (l: Loan) => (
              <div className="flex items-center gap-2.5">
                <Avatar name={l.borrower.name} src={l.borrower.photoUrl} size="sm" />
                <div className="min-w-0">
                  <span className="block truncate text-ink">{l.borrower.name}</span>
                  <span className="block text-xs text-muted">
                    {humanize(l.borrower.kind)} · <span className="font-mono">{l.borrower.code}</span>
                  </span>
                </div>
              </div>
            ),
          } satisfies Column<Loan>,
        ]
      : []),
    {
      key: 'borrowed',
      header: t('Borrowed'),
      sortKey: 'borrowedOn',
      hideBelow: 'lg',
      cell: (l) => formatDate(l.borrowedOn),
    },
    {
      key: 'due',
      header: t('Due'),
      sortKey: 'dueOn',
      cell: (l) => (
        <div>
          <span className="block">{formatDate(l.dueOn)}</span>
          {l.state !== 'returned' ? (
            <span className={l.state === 'overdue' ? 'text-xs text-danger-700' : 'text-xs text-muted'}>
              {relativeDue(l.dueOn)}
            </span>
          ) : null}
          {l.renewCount > 0 ? (
            <Badge size="sm" tone="brand" className="mt-0.5">
              {t('Renewed')}
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: 'returned',
      header: t('Returned'),
      sortKey: 'returnedOn',
      hideBelow: 'xl',
      cell: (l) => formatDate(l.returnedOn),
    },
    { key: 'state', header: t('Status'), cell: (l) => <LoanStateBadge loan={l} /> },
    {
      key: 'fine',
      header: t('Fine'),
      sortKey: 'fineAmount',
      hideBelow: 'md',
      cell: (l) => <FineCell loan={l} currency={currency} />,
    },
    ...(actions
      ? [
          {
            key: 'actions',
            header: <span className="sr-only">{t('Actions')}</span>,
            align: 'right' as const,
            cell: (l: Loan) => <LoanActions loan={l} currency={currency} />,
          },
        ]
      : []),
  ]

  return (
    <DataTable
      caption={caption}
      columns={columns}
      rows={loans}
      getRowId={(l) => l.id}
      loading={loading}
      error={error}
      onRetry={onRetry}
      sort={sort}
      onSortChange={onSortChange}
      skeletonRows={5}
      empty={empty}
      mobileCard={(l) => (
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <BookCover title={l.book.title} src={l.book.coverUrl} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-ink">{l.book.title}</p>
              {showBorrower ? (
                <p className="truncate text-xs text-muted">
                  {l.borrower.name} · {humanize(l.borrower.kind)}
                </p>
              ) : null}
              <p className="mt-1 text-xs text-muted">
                {t('Due {{date}}', { date: formatDate(l.dueOn) })}
                {l.returnedOn ? ` · ${t('returned {{date}}', { date: formatDate(l.returnedOn) })}` : ''}
              </p>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <LoanStateBadge loan={l} />
                <FineCell loan={l} currency={currency} />
              </div>
            </div>
          </div>
          {actions ? <LoanActions loan={l} currency={currency} /> : null}
        </div>
      )}
    />
  )
}
